import api from '../api/api';

/**
 * Prefijos estandarizados del sistema POSent.
 * IMPORTANTE: Todo QR generado por el sistema debe usar estos prefijos para
 * garantizar que el escáner de cámara identifique el tipo de código de forma
 * unívoca, sin depender de búsquedas ambiguas en el backend.
 */
export const POSENT_PREFIXES = {
  SALE: 'POSENT:SALE:',
  PRODUCT: 'POSENT:PROD:',
};

/**
 * Genera el valor de QR óptimo para una FACTURA o VENTA.
 * Formato: POSENT:SALE:<id>
 * Si no hay _id, usa invoice_number como fallback.
 */
export const formatSaleQrValue = (sale) => {
  if (!sale) return 'POSENT:SALE:UNKNOWN';
  const id = sale._id || sale.id;
  const num = sale.numero_venta || sale.invoice_number || sale.ticketNumber;
  const identifier = id || num || 'UNKNOWN';
  return `${POSENT_PREFIXES.SALE}${identifier}`;
};

/**
 * Genera el valor de QR óptimo para un PRODUCTO.
 * Formato: POSENT:PROD:<code>
 * Prioriza barcode > code > sku > id derivado.
 */
export const formatProductQrValue = (product) => {
  if (!product) return `${POSENT_PREFIXES.PRODUCT}000000`;
  const code =
    product.barcode ||
    product.codigo_barras ||
    product.code ||
    product.codigo ||
    product.sku ||
    (product._id ? `PRD-${String(product._id).slice(-8).toUpperCase()}` : '000000');
  return `${POSENT_PREFIXES.PRODUCT}${code}`;
};

/**
 * Genera el código puro (sin prefijo) para uso en código de BARRAS (EAN/Code128).
 * Los lectores de código de barras físicos esperan solo el valor, sin prefijos URI.
 */
export const formatProductCodeValue = (product) => {
  if (!product) return '000000';
  return (
    product.barcode ||
    product.codigo_barras ||
    product.code ||
    product.codigo ||
    product.sku ||
    (product._id ? `PRD-${String(product._id).slice(-8).toUpperCase()}` : '000000')
  );
};

/**
 * Normaliza y extrae el identificador útil de cualquier código escaneado.
 * Soporta: URLs completas, esquemas URI (POSENT:*), prefijos heredados,
 * patrones de factura y códigos de barras directos.
 *
 * @returns {{ code: string, hint: 'sale'|'product'|null }}
 */
export const sanitizeScannedCode = (raw) => {
  if (!raw) return { code: '', hint: null };
  let str = String(raw).trim();

  // ── URLs completas (escaneadas con cámara desde QR web) ──
  if (str.startsWith('http://') || str.startsWith('https://')) {
    try {
      const url = new URL(str);

      const saleParam = url.searchParams.get('sale') || url.searchParams.get('invoice') || url.searchParams.get('saleId');
      if (saleParam) return { code: saleParam.trim(), hint: 'sale' };

      const prodParam = url.searchParams.get('product') || url.searchParams.get('prod') || url.searchParams.get('sku');
      if (prodParam) return { code: prodParam.trim(), hint: 'product' };

      const segments = url.pathname.split('/').filter(Boolean);
      const lastSeg = segments[segments.length - 1];
      const prevSeg = segments[segments.length - 2];

      if (prevSeg === 'sales' || prevSeg === 'facturas' || prevSeg === 'invoice') {
        return { code: lastSeg, hint: 'sale' };
      }
      if (prevSeg === 'products' || prevSeg === 'productos') {
        return { code: lastSeg, hint: 'product' };
      }
      if (lastSeg) str = lastSeg;
    } catch (e) {
      // Continúa con el string crudo
    }
  }

  // ── Prefijos estandarizados del sistema POSent ──
  if (str.startsWith(POSENT_PREFIXES.SALE)) {
    return { code: str.slice(POSENT_PREFIXES.SALE.length).trim(), hint: 'sale' };
  }
  if (str.startsWith(POSENT_PREFIXES.PRODUCT)) {
    return { code: str.slice(POSENT_PREFIXES.PRODUCT.length).trim(), hint: 'product' };
  }

  // ── Prefijos heredados / alternativos ──
  if (str.startsWith('POSENT:PRODUCT:')) {
    return { code: str.replace('POSENT:PRODUCT:', '').trim(), hint: 'product' };
  }
  if (str.startsWith('REPEAT:') || str.startsWith('SALE:') || str.startsWith('FACTURA:')) {
    return { code: str.replace(/^(REPEAT|SALE|FACTURA):/, '').trim(), hint: 'sale' };
  }
  if (str.startsWith('PROD:')) {
    return { code: str.replace('PROD:', '').trim(), hint: 'product' };
  }

  // ── Corrección de teclado (Escáner US en Windows Español/Latino) ──
  // En layout español, el guión '-' de la pistola física envía el carácter '\''
  if (str.includes("'")) {
    const cand = str.replace(/'/g, '-');
    const upperCand = cand.toUpperCase();
    if (
      upperCand.startsWith('FAC-') ||
      upperCand.startsWith('COVER-') ||
      upperCand.startsWith('PRD-') ||
      upperCand.startsWith('TKT-') ||
      upperCand.startsWith('INV-') ||
      upperCand.startsWith('COT-') ||
      upperCand.startsWith('TK-')
    ) {
      str = cand;
    }
  }

  // ── Patrones reconocidos de facturas / tickets ──
  const upper = str.toUpperCase();
  if (
    upper.startsWith('FAC-') ||
    upper.startsWith('FAC') ||
    upper.startsWith('F-') ||
    upper.startsWith('V-') ||
    upper.startsWith('COT-') ||
    upper.startsWith('TK-') ||
    upper.startsWith('TICK-') ||
    upper.startsWith('TKT-') ||
    upper.startsWith('REC-') ||
    upper.startsWith('INV-')
  ) {
    return { code: str, hint: 'sale' };
  }

  // ── Prefijo interno de ID derivado (PRD-XXXXXXXX) ──
  if (upper.startsWith('PRD-')) {
    return { code: str, hint: 'product' };
  }

  return { code: str, hint: null };
};

/**
 * Busca un producto en el catálogo local (en memoria).
 * Compara contra: barcode, code, codigo, sku, _id, nombre, y PRD-id derivado.
 * Tolera diferencias de teclado US/Español (' por -).
 */
export const findProductLocally = (code, products = []) => {
  if (!code || !Array.isArray(products) || products.length === 0) return null;
  const target = String(code).trim().toLowerCase();
  const targetNormalized = target.replace(/'/g, '-');

  return (
    products.find((p) => {
      const pCode = String(p.code || '').trim().toLowerCase();
      const pCodigo = String(p.codigo || '').trim().toLowerCase();
      const pBarcode = String(p.barcode || p.codigo_barras || '').trim().toLowerCase();
      const pSku = String(p.sku || '').trim().toLowerCase();
      const pId = String(p._id || p.id || '').trim().toLowerCase();
      const pName = String(p.name || p.nombre || '').trim().toLowerCase();
      // PRD-derivado: últimos 8 chars del _id en mayúscula
      const pPrdId = p._id ? `prd-${String(p._id).slice(-8).toLowerCase()}` : '';

      return (
        pCode === target || pCode === targetNormalized ||
        pCodigo === target || pCodigo === targetNormalized ||
        pBarcode === target || pBarcode === targetNormalized ||
        pSku === target || pSku === targetNormalized ||
        pId === target || pId === targetNormalized ||
        pName === target || pName === targetNormalized ||
        pPrdId === target || pPrdId === targetNormalized
      );
    }) || null
  );
};

/**
 * Resuelve cualquier código escaneado (cámara, imagen o lector de código de barras).
 * Estrategia de resolución por orden de prioridad:
 *  1. Si hint === 'sale' → buscar directamente en /sales/:code
 *  2. Si hint === 'product' o sin hint → buscar localmente primero
 *  3. ObjectId de MongoDB (24 hex) → probar product luego sale
 *  4. Búsqueda backend por código de producto (probando también variante con '-')
 *  5. Búsqueda backend por query string
 *  6. Último intento como venta
 *
 * @param {string} rawCode - Texto del código escaneado (con o sin prefijo)
 * @param {{ products?: Array }} options
 * @returns {Promise<{ type: 'product'|'sale'|'not_found', data?: any, rawCode: string, code: string }>}
 */
export const resolveScannedCode = async (rawCode, { products = [] } = {}) => {
  const { code, hint } = sanitizeScannedCode(rawCode);
  if (!code) {
    return { type: 'not_found', rawCode, code: '' };
  }

  const searchCandidates = [code];
  if (code.includes("'")) {
    searchCandidates.push(code.replace(/'/g, '-'));
  }

  // ── 1. Hint explícito: es una venta / factura ──
  if (hint === 'sale') {
    for (const c of searchCandidates) {
      try {
        const res = await api.get(`/sales/${encodeURIComponent(c)}`);
        if (res.data?.success && res.data?.data) {
          return { type: 'sale', data: res.data.data, rawCode, code: c };
        }
      } catch (err) {
        try {
          const searchRes = await api.get(`/sales?invoice_number=${encodeURIComponent(c)}&limit=1`);
          const hits = searchRes.data?.data;
          if (hits && (Array.isArray(hits) ? hits.length > 0 : hits)) {
            const sale = Array.isArray(hits) ? hits[0] : hits;
            return { type: 'sale', data: sale, rawCode, code: c };
          }
        } catch (e2) {
          console.warn('Error buscando venta por número:', e2);
        }
      }
    }
  }

  // ── 2. Hint producto o sin hint → catálogo local primero ──
  if (hint === 'product' || hint === null) {
    const localProduct = findProductLocally(code, products);
    if (localProduct) {
      return { type: 'product', data: localProduct, rawCode, code };
    }
  }

  // ── 3. ObjectId MongoDB (24 caracteres hex) → puede ser producto o venta ──
  const is24Hex = /^[0-9a-fA-F]{24}$/.test(code);
  if (is24Hex) {
    try {
      const prodRes = await api.get(`/products/${encodeURIComponent(code)}`);
      if (prodRes.data?.success && prodRes.data?.data) {
        return { type: 'product', data: prodRes.data.data, rawCode, code };
      }
    } catch (e) { /* no era producto */ }

    try {
      const saleRes = await api.get(`/sales/${encodeURIComponent(code)}`);
      if (saleRes.data?.success && saleRes.data?.data) {
        return { type: 'sale', data: saleRes.data.data, rawCode, code };
      }
    } catch (e) { /* no era venta */ }
  }

  // ── 4. Buscar en backend como producto por código exacto ──
  for (const c of searchCandidates) {
    try {
      const prodRes = await api.get(`/products/${encodeURIComponent(c)}`);
      if (prodRes.data?.success && prodRes.data?.data) {
        return { type: 'product', data: prodRes.data.data, rawCode, code: c };
      }
    } catch (e) { /* continuar */ }
  }

  // ── 5. Búsqueda por query string en productos ──
  for (const c of searchCandidates) {
    try {
      const searchRes = await api.get(`/products?search=${encodeURIComponent(c)}&limit=5`);
      if (searchRes.data?.success && Array.isArray(searchRes.data?.data) && searchRes.data.data.length > 0) {
        const exactMatch = findProductLocally(c, searchRes.data.data) || searchRes.data.data[0];
        return { type: 'product', data: exactMatch, rawCode, code: c };
      }
    } catch (e) {
      console.warn('Error buscando productos en backend:', e);
    }
  }

  // ── 6. Último intento: verificar si coincide con una venta ──
  if (hint !== 'sale') {
    for (const c of searchCandidates) {
      try {
        const saleRes = await api.get(`/sales/${encodeURIComponent(c)}`);
        if (saleRes.data?.success && saleRes.data?.data) {
          return { type: 'sale', data: saleRes.data.data, rawCode, code: c };
        }
      } catch (e) { /* tampoco fue venta */ }

      try {
        const searchRes = await api.get(`/sales?invoice_number=${encodeURIComponent(c)}&limit=1`);
        const hits = searchRes.data?.data;
        if (hits && (Array.isArray(hits) ? hits.length > 0 : hits)) {
          const sale = Array.isArray(hits) ? hits[0] : hits;
          return { type: 'sale', data: sale, rawCode, code: c };
        }
      } catch (e2) { /* continuar */ }
    }
  }

  return { type: 'not_found', rawCode, code };
};

export default resolveScannedCode;
